import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useChannel } from '@/context/ChannelContext'
import {
  getShowcasePosts,
  deindexPost,
  reindexPost,
  PostStatus,
  PostsCountsByStatus,
  PostsSort,
  ShowcasePost,
} from '@/api/showcase'
import { track, trackError } from '@/lib/analytics'

/**
 * A4 — Список постов канала. Использует B6 (контракт зафиксирован, реальный
 * endpoint появится через ~24ч от B). Тумблер deindex дёргает B8 (готов).
 *
 * При клике по посту переходим на A5 (карточка) — путь `/showcase/posts/:postId`.
 *
 * Поля и фильтры по §6 спеки + контракту B6:
 * - status: published | queued | deindexed | rejected
 * - sort: published_at_desc | visits_desc | score_desc
 * - поиск по своим постам (клиентский, по title — сервер пока не поддерживает)
 */
export function PostsPage() {
  const { selectedChannel } = useChannel()
  const navigate = useNavigate()
  const [status, setStatus] = useState<PostStatus | undefined>(undefined)
  const [sort, setSort] = useState<PostsSort>('published_at_desc')
  const [search, setSearch] = useState('')
  const [items, setItems] = useState<ShowcasePost[] | null>(null)
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState<PostsCountsByStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set())

  const channelUUID = selectedChannel?.uuid

  const fetchPosts = useCallback(async () => {
    if (!channelUUID) return
    setError(null)
    try {
      const res = await getShowcasePosts(channelUUID, { status, sort, limit: 50 })
      setItems(res.items)
      setTotal(res.total)
      // counts_by_status — приходит при каждом запросе и не сужается фильтром.
      // B6 перешёл на camelCase (`qualityRejected`), но мой PostStatus enum
      // = snake_case (`quality_rejected`). Нормализуем чтение под snake чтобы
      // type Record<PostStatus, number> совпадал. Также поддерживаем legacy
      // snake_case на случай fallback'а от транзита.
      const raw = (res.countsByStatus
        ?? (res as unknown as { counts_by_status?: Record<string, number> }).counts_by_status
        ?? null) as Record<string, number> | null
      if (raw) {
        setCounts({
          queued: raw.queued ?? 0,
          processing: raw.processing ?? 0,
          published: raw.published ?? 0,
          quality_rejected: raw.qualityRejected ?? raw.quality_rejected ?? 0,
          deindexed: raw.deindexed ?? 0,
          failed: raw.failed ?? 0,
        })
      }
    } catch (e) {
      trackError(e, 'showcase-posts-list')
      setError(e instanceof Error ? e.message : 'Не удалось загрузить посты')
      setItems([])
    }
  }, [channelUUID, status, sort])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  async function handleDeindex(post: ShowcasePost) {
    if (!channelUUID) return
    // Оптимистично — статус меняем мгновенно, при ошибке откатим
    const prev = items
    setItems((cur) => cur?.map((p) => (p.postId === post.postId ? { ...p, status: 'deindexed' } : p)) ?? null)
    setUpdatingIds((s) => new Set(s).add(post.postId))
    try {
      await deindexPost(channelUUID, post.postId)
      track('showcase_post_deindexed', {
        postId: post.postId,
        sourcePostId: post.sourcePostId,
        message: `пост убран из поиска · ${post.title.slice(0, 40)}`,
      })
    } catch (e) {
      trackError(e, 'showcase-deindex-post')
      // Откатываем оптимистичное обновление
      setItems(prev)
      setError(e instanceof Error ? e.message : 'Не удалось убрать пост')
    } finally {
      setUpdatingIds((s) => {
        const next = new Set(s)
        next.delete(post.postId)
        return next
      })
    }
  }

  async function handleReindex(post: ShowcasePost) {
    if (!channelUUID) return
    // Оптимистично — переводим в 'queued'. Бэк решит queued/processing/published
    // (см. response.status: restored_from_archive | queued_for_render | already_published).
    const prev = items
    setItems((cur) => cur?.map((p) => (p.postId === post.postId ? { ...p, status: 'queued' } : p)) ?? null)
    setUpdatingIds((s) => new Set(s).add(post.postId))
    try {
      const res = await reindexPost(channelUUID, post.postId)
      // Если portal сразу восстановил из архива — финальный status уже 'published'.
      // Если в очередь — оставляем 'queued', polling refetch подхватит.
      if (res.status === 'restored_from_archive' || res.status === 'already_published') {
        setItems((cur) => cur?.map((p) => (p.postId === post.postId ? { ...p, status: 'published', deindexedAt: null } : p)) ?? null)
      }
      track('showcase_post_reindexed', {
        postId: post.postId,
        sourcePostId: post.sourcePostId,
        reindexStatus: res.status,
        estimatedSeconds: res.estimatedSeconds,
        message: `пост возвращён в поиск · ${res.status} · ${post.title.slice(0, 40)}`,
      })
    } catch (e) {
      trackError(e, 'showcase-reindex-post')
      setItems(prev)
      setError(e instanceof Error ? e.message : 'Не удалось вернуть пост в поиск')
    } finally {
      setUpdatingIds((s) => {
        const next = new Set(s)
        next.delete(post.postId)
        return next
      })
    }
  }

  function handleRowClick(post: ShowcasePost) {
    track('showcase_post_open', { postId: post.postId })
    // Прокидываем post в location.state — A5 покажет данные мгновенно без
    // дополнительного запроса. Если юзер придёт по прямому URL — A5 сам
    // догрузит через fetchOne (см. PostCardPage).
    navigate(`/showcase/posts/${post.postId}`, { state: { post } })
  }

  const filtered = (items ?? []).filter((p) =>
    search ? p.title.toLowerCase().includes(search.toLowerCase()) : true,
  )

  return (
    <div className="posts">
      <Toolbar
        status={status}
        sort={sort}
        search={search}
        total={total}
        filtered={filtered.length}
        counts={counts}
        onStatus={setStatus}
        onSort={setSort}
        onSearch={setSearch}
      />

      {error && (
        <div className="posts-error">{error}</div>
      )}

      {items === null ? (
        <ListSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState hasFilter={!!status || !!search} />
      ) : (
        <div className="posts-list">
          {filtered.map((post) => (
            <PostRow
              key={post.postId}
              post={post}
              updating={updatingIds.has(post.postId)}
              onDeindex={handleDeindex}
              onReindex={handleReindex}
              onClick={handleRowClick}
            />
          ))}
        </div>
      )}

      <style>{`
        .posts { display: flex; flex-direction: column; gap: 14px; }
        .posts-list { display: flex; flex-direction: column; gap: 8px; }
        .posts-error {
          padding: 10px 14px;
          background: rgba(229, 70, 70, 0.1);
          border: 1px solid rgba(229, 70, 70, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
      `}</style>
    </div>
  )
}

// ───────── Toolbar (filter, sort, search) ─────────

function Toolbar({
  status, sort, search, total, filtered, counts,
  onStatus, onSort, onSearch,
}: {
  status: PostStatus | undefined
  sort: PostsSort
  search: string
  total: number
  filtered: number
  counts: PostsCountsByStatus | null
  onStatus: (s: PostStatus | undefined) => void
  onSort: (s: PostsSort) => void
  onSearch: (s: string) => void
}) {
  // «Все» = сумма всех счётчиков. «Готовится» = queued + processing (визуальное
  // объединение фаз). Остальные чипы — 1:1 со статусом.
  const total_ = counts
    ? counts.queued + counts.processing + counts.published + counts.quality_rejected + counts.deindexed + counts.failed
    : null
  const inQueue = counts ? counts.queued + counts.processing : null

  return (
    <div className="tbar">
      <div className="tbar-chips">
        <Chip active={!status} count={total_} onClick={() => onStatus(undefined)}>Все</Chip>
        <Chip active={status === 'published'} count={counts?.published ?? null} onClick={() => onStatus('published')}>В&nbsp;поиске</Chip>
        <Chip active={(status as string) === 'queued,processing' || status === 'queued' || status === 'processing'} count={inQueue} onClick={() => onStatus('queued,processing' as PostStatus)}>Готовится</Chip>
        <Chip active={status === 'deindexed'} count={counts?.deindexed ?? null} onClick={() => onStatus('deindexed')}>Скрыты</Chip>
        <Chip active={status === 'quality_rejected'} count={counts?.quality_rejected ?? null} onClick={() => onStatus('quality_rejected')}>Не&nbsp;прошли</Chip>
        <Chip active={status === 'failed'} count={counts?.failed ?? null} onClick={() => onStatus('failed')}>Ошибки</Chip>
      </div>

      <div className="tbar-controls">
        <div className="tbar-search">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            placeholder="Поиск по заголовку"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>

        <select
          className="tbar-sort"
          value={sort}
          onChange={(e) => onSort(e.target.value as PostsSort)}
          aria-label="Сортировка"
        >
          <option value="published_at_desc">Сначала свежие</option>
          <option value="visits_desc">Больше читателей</option>
          <option value="score_desc">Выше качество</option>
        </select>
      </div>

      <div className="tbar-counter">
        {filtered === total ? (
          <>{total}&nbsp;{pluralPosts(total)}</>
        ) : (
          <>Показано {filtered} из {total}</>
        )}
      </div>

      <style>{`
        .tbar {
          display: grid;
          grid-template-areas:
            "chips chips"
            "controls counter";
          grid-template-columns: 1fr auto;
          gap: 12px;
          align-items: center;
        }
        @media (max-width: 700px) {
          .tbar {
            grid-template-areas: "chips" "controls" "counter";
            grid-template-columns: 1fr;
          }
        }
        .tbar-chips {
          grid-area: chips;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .tbar-controls {
          grid-area: controls;
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .tbar-counter {
          grid-area: counter;
          font-size: 12.5px;
          color: var(--tg-mute, #5b6678);
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .tbar-search {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
        }
        .tbar-search svg { width: 14px; height: 14px; color: var(--tg-mute, #5b6678); flex-shrink: 0; }
        .tbar-search input {
          border: 0;
          background: transparent;
          font-family: inherit;
          font-size: 13.5px;
          color: var(--tg-ink, #111);
          flex: 1;
          min-width: 0;
          outline: none;
        }
        .tbar-search input::placeholder { color: var(--tg-mute, #5b6678); }
        .tbar-sort {
          padding: 8px 12px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          font-family: inherit;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          cursor: pointer;
        }
      `}</style>
    </div>
  )
}

function Chip({
  active, count, onClick, children,
}: {
  active: boolean
  count?: number | null
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button className={`chip ${active ? 'on' : ''}`} onClick={onClick} type="button">
      <span className="chip-label">{children}</span>
      {typeof count === 'number' && <span className="chip-count">{count.toLocaleString('ru-RU')}</span>}
      <style>{`
        .chip {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 999px;
          font-family: inherit;
          font-size: 12.5px;
          font-weight: 600;
          color: var(--tg-mute, #5b6678);
          cursor: pointer;
          white-space: nowrap;
          transition: background 150ms, border-color 150ms, color 150ms;
        }
        .chip:hover { color: var(--tg-ink, #111); }
        .chip.on {
          background: var(--tg-ink, #111);
          color: white;
          border-color: var(--tg-ink, #111);
        }
        .chip-count {
          font-size: 11px;
          font-weight: 700;
          padding: 1px 6px;
          border-radius: 999px;
          background: var(--tg-line-2, #f0f0f4);
          color: var(--tg-mute, #5b6678);
          font-variant-numeric: tabular-nums;
        }
        .chip.on .chip-count {
          background: rgba(255,255,255,0.18);
          color: white;
        }
      `}</style>
    </button>
  )
}

// ───────── Post Row ─────────

function PostRow({
  post, updating, onDeindex, onReindex, onClick,
}: {
  post: ShowcasePost
  updating: boolean
  onDeindex: (p: ShowcasePost) => void
  onReindex: (p: ShowcasePost) => void
  onClick: (p: ShowcasePost) => void
}) {
  function handleToggle(e: React.MouseEvent) {
    e.stopPropagation()
    if (post.status === 'published') onDeindex(post)
    else if (post.status === 'deindexed') onReindex(post)
  }

  return (
    <article
      className="row"
      onClick={() => onClick(post)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(post) }}
    >
      <div className="row-main">
        <div className="row-title-wrap">
          <StatusBadge status={post.status} />
          <h3 className="row-title">
            {post.title || (post.status === 'quality_rejected' ? 'Пост без заголовка' : 'Без заголовка')}
          </h3>
        </div>
        <div className="row-meta">
          {post.status === 'quality_rejected' ? (
            // Для quality_rejected нет страницы на портале → метрики/дата бесполезны.
            // Показываем только подсказку про причину; детали — в Overview-плашке (B10).
            <span className="row-qr-hint">AI-фильтр посчитал слишком коротким или невалидным для поиска</span>
          ) : post.status === 'failed' ? (
            <span className="row-qr-hint">Не удалось опубликовать — техническая ошибка</span>
          ) : (
            <>
              {post.publishedAt && (
                <span>опубликован {formatRuDate(new Date(post.publishedAt))}</span>
              )}
              {post.publishedAt && <span className="row-sep">·</span>}
              <span>
                <b>{post.visitsTotal.toLocaleString('ru-RU')}</b>&nbsp;{pluralReaders(post.visitsTotal)}
              </span>
              {post.qualityScore !== null && (
                <>
                  <span className="row-sep">·</span>
                  <span title="Quality score">качество&nbsp;{post.qualityScore}</span>
                </>
              )}
              {post.noindex && post.status === 'published' && (
                <>
                  <span className="row-sep">·</span>
                  <span className="row-noindex" title="Опубликован, но не индексируется">не&nbsp;в&nbsp;поиске</span>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="row-actions">
        {(post.status === 'published' || post.status === 'deindexed') && (
          <label className="toggle" onClick={(e) => e.stopPropagation()}>
            <span className="toggle-label">{post.status === 'published' ? 'Скрыть' : 'Вернуть'}</span>
            <input
              type="checkbox"
              checked={post.status === 'deindexed'}
              disabled={updating}
              onChange={() => {}}
              onClick={handleToggle}
              aria-label={post.status === 'published' ? 'Убрать из поиска' : 'Вернуть в поиск'}
            />
            <span className={`toggle-track ${post.status === 'deindexed' ? 'on' : ''}`}>
              <span className="toggle-thumb" />
            </span>
          </label>
        )}
        <svg className="row-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 6l6 6-6 6" />
        </svg>
      </div>

      <style>{`
        .row {
          display: flex;
          gap: 14px;
          align-items: center;
          padding: 14px 18px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          cursor: pointer;
          transition: border-color 150ms, transform 100ms;
        }
        .row:hover { border-color: var(--tg-line-3, #d0d0d8); }
        .row:active { transform: scale(0.998); }
        .row-main { flex: 1; min-width: 0; }
        .row-title-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }
        .row-title {
          font-size: 14.5px;
          font-weight: 700;
          color: var(--tg-ink, #111);
          margin: 0;
          line-height: 1.35;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        .row-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          font-size: 12px;
          color: var(--tg-mute, #5b6678);
          font-variant-numeric: tabular-nums;
        }
        .row-meta b {
          color: var(--tg-ink, #111);
          font-weight: 700;
        }
        .row-sep { opacity: 0.5; }
        .row-qr-hint {
          color: var(--tg-mute, #5b6678);
          font-style: italic;
          font-size: 12.5px;
        }
        .row-actions {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-shrink: 0;
        }
        .row-chevron {
          width: 16px;
          height: 16px;
          color: var(--tg-mute, #5b6678);
        }
        .toggle {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .toggle input[type="checkbox"] { position: absolute; opacity: 0; pointer-events: none; }
        .toggle-label {
          font-size: 12px;
          color: var(--tg-mute, #5b6678);
          font-weight: 600;
        }
        .toggle-track {
          width: 36px;
          height: 20px;
          border-radius: 999px;
          background: var(--tg-line-3, #d0d0d8);
          position: relative;
          transition: background 150ms;
        }
        .toggle-track.on { background: #e54646; }
        .toggle-thumb {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 16px;
          height: 16px;
          background: white;
          border-radius: 50%;
          transition: transform 150ms;
          box-shadow: 0 1px 2px rgba(0,0,0,0.15);
        }
        .toggle-track.on .toggle-thumb { transform: translateX(16px); }
        @media (max-width: 600px) {
          .row { padding: 12px 14px; }
          .toggle-label { display: none; }
        }
      `}</style>
    </article>
  )
}

function StatusBadge({ status }: { status: PostStatus }) {
  const map: Record<PostStatus, { label: string; cls: string }> = {
    published: { label: 'в поиске', cls: 'b-pub' },
    queued: { label: 'в очереди', cls: 'b-q' },
    processing: { label: 'готовится', cls: 'b-q' },
    deindexed: { label: 'скрыт', cls: 'b-d' },
    quality_rejected: { label: 'не прошёл проверку', cls: 'b-r' },
    failed: { label: 'ошибка', cls: 'b-e' },
  }
  const m = map[status]
  return (
    <span className={`badge ${m.cls}`}>
      {m.label}
      <style>{`
        .badge {
          padding: 2px 8px;
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border-radius: 4px;
          flex-shrink: 0;
        }
        .b-pub { background: rgba(0, 168, 96, 0.15); color: #008a52; }
        .b-q { background: rgba(0, 102, 255, 0.12); color: #0052cc; }
        .b-d { background: rgba(91, 102, 120, 0.15); color: #5b6678; }
        .b-r { background: rgba(229, 70, 70, 0.12); color: #c53030; }
        .b-e { background: rgba(229, 70, 70, 0.18); color: #a01919; }
      `}</style>
    </span>
  )
}

// ───────── Skeleton / Empty ─────────

function ListSkeleton() {
  return (
    <div className="skel-list">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="skel-row" />
      ))}
      <style>{`
        .skel-list { display: flex; flex-direction: column; gap: 8px; }
        .skel-row {
          height: 76px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          animation: skel-pulse 1.4s ease-in-out infinite;
        }
        @keyframes skel-pulse { 50% { opacity: 0.5; } }
      `}</style>
    </div>
  )
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="empty">
      <div className="empty-icon">📭</div>
      <h3 className="empty-title">
        {hasFilter ? 'Ничего не найдено' : 'Пока нет постов'}
      </h3>
      <p className="empty-sub">
        {hasFilter
          ? 'Попробуйте сбросить фильтры или поискать иначе.'
          : 'Здесь появятся посты канала, когда они попадут в Витрину.'}
      </p>
      <style>{`
        .empty {
          padding: 48px 24px;
          background: var(--tg-surface, #fff);
          border: 1px dashed var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          text-align: center;
        }
        .empty-icon { font-size: 36px; margin-bottom: 8px; }
        .empty-title { font-size: 16px; font-weight: 700; margin: 0 0 4px; color: var(--tg-ink, #111); }
        .empty-sub { font-size: 13px; color: var(--tg-mute, #5b6678); margin: 0; max-width: 320px; margin: 0 auto; }
      `}</style>
    </div>
  )
}

// ───────── helpers ─────────

const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
function formatRuDate(d: Date): string {
  return `${d.getDate()} ${MONTHS_RU[d.getMonth()]}`
}

function pluralReaders(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'читатель'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'читателя'
  return 'читателей'
}

function pluralPosts(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'пост'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'поста'
  return 'постов'
}
